/*
  Product page: variant sync, quantity limits, the mobile swipe gallery, the
  sticky buy bar and the contact dialogs (offer, notify, ask, viewing).

  Document-level listeners are bound once; per-section work (observers,
  gallery state) runs in init() for every [data-main-product] and again on
  shopify:section:load, so the theme editor can re-render the section.
*/
(() => {
  'use strict';

  if (window.psjProduct) return;
  window.psjProduct = true;

  const PENDING_KEY = 'psj:contact-pending';
  const PENDING_TTL = 30 * 60 * 1000;
  const cleanups = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const $$ = (root, selector) => [...root.querySelectorAll(selector)];
  const sectionOf = (el) => el.closest('[data-main-product]');

  const session = {
    get() {
      try {
        return JSON.parse(sessionStorage.getItem(PENDING_KEY));
      } catch {
        return null;
      }
    },
    set(value) {
      try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(value));
      } catch {
        /* storage blocked: the #form-id in the URL still identifies the form */
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(PENDING_KEY);
      } catch {
        /* ignore */
      }
    },
  };

  /* ---------- Quantity: never more than exists ---------- */

  // Keeps the value inside [min, max] and greys out the step that would leave it.
  const clampQuantity = (input) => {
    const min = parseInt(input.min, 10) || 1;
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    const value = Math.min(max, Math.max(min, parseInt(input.value, 10) || min));
    if (String(value) !== input.value) input.value = value;

    const wrapper = input.closest('[data-quantity]');
    const down = wrapper?.querySelector('[data-direction="down"]');
    const up = wrapper?.querySelector('[data-direction="up"]');
    if (down) down.disabled = value <= min;
    if (up) up.disabled = value >= max;
  };

  // limit: a number when stock is tracked with DENY, null when unlimited.
  const applyQuantityLimit = (root, available, limit) => {
    const wrapper = root.querySelector('[data-quantity]');
    const input = wrapper?.querySelector('[data-quantity-input]');
    if (!wrapper || !input) return;

    const hide = !available || (limit !== null && limit <= 1);
    wrapper.hidden = hide;
    input.disabled = hide;
    if (hide) input.value = 1;

    if (limit !== null) input.max = Math.max(1, limit);
    else input.removeAttribute('max');
    clampQuantity(input);
  };

  /* ---------- Variant change ---------- */

  const onVariantChange = (root, select) => {
    const option = select.selectedOptions[0];
    if (!option) return;
    const data = option.dataset;
    const available = data.available ? data.available === 'true' : !option.disabled;
    const limit = data.inventory === '' || data.inventory === undefined ? null : parseInt(data.inventory, 10);

    if (data.price) {
      $$(root, '[data-product-price] .price__current, [data-sticky-price], [data-asking-price]').forEach((el) => {
        el.textContent = data.price;
      });
      $$(root, '[data-asking-input]').forEach((el) => {
        el.value = data.price;
      });
      // The SalesChannelHub offer form posts the plain number.
      if (data.priceRaw) {
        $$(root, '[data-asking-raw-input]').forEach((el) => {
          el.value = data.priceRaw;
        });
      }
    }
    $$(root, '[data-variant-id-input]').forEach((el) => {
      el.value = option.value;
    });

    const afterpay = root.querySelector('[data-afterpay-wrapper]');
    if (afterpay && data.priceRaw) {
      afterpay.querySelector('afterpay-placement')?.setAttribute('data-amount', data.priceRaw);
      const cents = parseInt(data.priceCents, 10);
      const min = parseInt(afterpay.dataset.minCents, 10);
      const max = parseInt(afterpay.dataset.maxCents, 10);
      afterpay.hidden = Number.isFinite(cents) && (cents < min || cents > max);
    }

    const addButton = root.querySelector('[data-product-add]');
    if (addButton) {
      addButton.disabled = !available;
      addButton.textContent = available ? addButton.dataset.addText : addButton.dataset.soldOutText;
    }
    applyQuantityLimit(root, available, limit);
    root.dataset.inventoryLimit = limit === null ? '' : String(limit);

    // In stock you can haggle; out of stock you can ask to be told when it returns.
    $$(root, '[data-offer-toggle], [data-payment-button], [data-sticky-add]').forEach((el) => {
      el.hidden = !available;
    });
    $$(root, '[data-notify-toggle], [data-sticky-notify], [data-sticky-soldout]').forEach((el) => {
      el.hidden = available;
    });

    const sku = data.sku || '';
    $$(root, '[data-sku-value]').forEach((el) => {
      el.textContent = sku;
    });
    $$(root, '[data-sku-input]').forEach((el) => {
      el.value = sku;
    });
    const skuRow = root.querySelector('[data-product-sku]');
    if (skuRow) skuRow.hidden = !sku;

    if (data.url) {
      $$(root, '[data-url-input]').forEach((el) => {
        el.value = data.url;
      });
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    history.replaceState(history.state, '', url);
  };

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-variant-select]');
    if (select) {
      const root = sectionOf(select);
      if (root) onVariantChange(root, select);
      return;
    }
    const quantity = event.target.closest?.('[data-quantity-input]');
    if (quantity) clampQuantity(quantity);
  });

  /* ---------- Contact dialogs (offer, notify, ask, viewing) ---------- */

  // The offer amount gets a plain-language message instead of "match the format".
  document.addEventListener('input', (event) => {
    const amount = event.target.closest?.('[data-offer-amount]');
    if (!amount) return;
    amount.setCustomValidity('');
    const value = parseFloat(amount.value.replace(/[$,\s]/g, ''));
    if (amount.validity.patternMismatch || (amount.value.trim() && !(value > 0))) {
      amount.setCustomValidity(amount.dataset.invalidMessage || '');
    }
  });

  // Capture phase: runs before Shopify's hCaptcha listeners, which hold the
  // submit back while they fetch a token and then post the form natively.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest?.('form[data-product-contact]');
      if (!form) return;

      session.set({ form: form.id, path: window.location.pathname, time: Date.now() });

      const button = form.querySelector('[type="submit"]');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      const label = button.textContent;
      button.setAttribute('aria-disabled', 'true');
      button.textContent = button.dataset.sendingText || label;
      // If the post never leaves (captcha still loading, offline), hand the button back.
      setTimeout(() => {
        button.removeAttribute('aria-disabled');
        button.textContent = label;
      }, 8000);
    },
    true
  );

  // Closing a sent dialog resets it, so it can be used again for another piece.
  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[data-contact-dialog]')) return;
      if (dialog.classList.contains('is-sent')) {
        dialog.classList.remove('is-sent');
        dialog.querySelectorAll('[data-contact-status]').forEach((el) => el.remove());
        dialog.querySelector('form')?.reset();
      }
      if (document.activeElement === document.body || !document.activeElement) {
        const toggle = [...document.querySelectorAll(`[aria-controls="${dialog.id}"]`)].find(
          (el) => !el.hidden && el.offsetParent !== null
        );
        toggle?.focus();
      }
    },
    true
  );

  /*
    After the contact POST Shopify redirects back here with contact_posted=true
    and form.posted_successfully? is true for every contact form on the page.
    Only the form that was sent keeps its status: it is identified by the
    #form-id the browser carries over from the form action, or by the note
    left in sessionStorage on submit (covers a detour through /challenge).
  */
  const handleContactReturn = (root) => {
    const forms = $$(root, 'form[data-product-contact]');
    if (!forms.length) return;

    let target = null;
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (hash) target = forms.find((form) => form.id === hash) || null;

    const pending = session.get();
    const pendingValid =
      pending && pending.path === window.location.pathname && Date.now() - pending.time < PENDING_TTL;
    if (!target && pendingValid) target = forms.find((form) => form.id === pending.form) || null;

    forms.forEach((form) => {
      if (form !== target) form.querySelectorAll('[data-contact-status]').forEach((el) => el.remove());
    });
    if (!target) return;

    const status = target.querySelector('[data-contact-status]');
    if (!status) return;
    session.clear();

    const dialog = target.closest('dialog');
    if (!dialog) return;
    if (status.dataset.contactStatus === 'success') dialog.classList.add('is-sent');
    if (!dialog.open) dialog.showModal();
    status.focus();

    const url = new URL(window.location.href);
    url.searchParams.delete('contact_posted');
    url.hash = '';
    history.replaceState(history.state, '', url);
  };

  /* ---------- Mobile swipe gallery ---------- */

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-product-gallery]');
    const track = gallery?.querySelector('[data-gallery-track]');
    const slides = track ? $$(track, '[data-gallery-slide]') : [];
    if (slides.length < 2) return () => {};

    const counter = gallery.querySelector('[data-gallery-current]');
    const status = gallery.querySelector('[data-gallery-status]');
    const dots = $$(gallery, '[data-gallery-dot]');
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    const controller = new AbortController();
    const { signal } = controller;
    let index = 0;
    let settleTimer;

    const render = (i) => {
      index = i;
      if (counter) counter.textContent = i + 1;
      dots.forEach((dot, n) => dot.setAttribute('aria-current', n === i ? 'true' : 'false'));
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    };

    // Announce once the swipe settles, not for every slide it passes.
    const announce = () => {
      if (!status) return;
      status.textContent = status.dataset.template.replace('[current]', index + 1);
    };

    const go = (i) => {
      const target = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({
        left: target * track.clientWidth,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    };

    let frame;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          if (!track.clientWidth) return;
          const i = Math.round(track.scrollLeft / track.clientWidth);
          if (i !== index) render(i);
        });
        clearTimeout(settleTimer);
        settleTimer = setTimeout(announce, 250);
      },
      { passive: true, signal }
    );

    prev?.addEventListener('click', () => go(index - 1), { signal });
    next?.addEventListener('click', () => go(index + 1), { signal });
    dots.forEach((dot) => dot.addEventListener('click', () => go(parseInt(dot.dataset.galleryDot, 10)), { signal }));

    render(0);
    return () => {
      controller.abort();
      clearTimeout(settleTimer);
      cancelAnimationFrame(frame);
    };
  };

  /* ---------- Sticky buy bar ---------- */

  const setStickyOffset = (px) => {
    document.documentElement.style.setProperty('--psj-sticky-bottom', `${px}px`);
  };

  const initStickyBar = (root) => {
    const bar = root.querySelector('[data-sticky-bar]');
    const anchor = root.querySelector('[data-product-add]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return () => {};

    const setVisible = (visible) => {
      bar.classList.toggle('is-visible', visible);
      bar.inert = !visible;
      setStickyOffset(visible ? bar.offsetHeight : 0);
    };

    // Shown only once the main button has scrolled up and away — never while
    // it is still below the fold, and never on top of it. Stretching the root
    // far below the viewport leaves one boundary (the viewport's top edge), so
    // a jump straight past the button (End key, anchor link) still registers.
    const io = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { rootMargin: '0px 0px 100000px 0px' }
    );
    io.observe(anchor);

    const ro = 'ResizeObserver' in window
      ? new ResizeObserver(() => {
          if (bar.classList.contains('is-visible')) setStickyOffset(bar.offsetHeight);
        })
      : null;
    ro?.observe(bar);

    // Mirror the main button's "Adding…" state onto the bar's copy.
    const stickyAdd = bar.querySelector('[data-sticky-add]');
    const mirror = () => {
      if (!stickyAdd) return;
      const busy = anchor.getAttribute('aria-disabled') === 'true';
      if (busy) stickyAdd.setAttribute('aria-disabled', 'true');
      else stickyAdd.removeAttribute('aria-disabled');
      stickyAdd.textContent = busy ? anchor.textContent : anchor.dataset.addText;
    };
    const mo = new MutationObserver(mirror);
    mo.observe(anchor, { attributes: true, attributeFilter: ['aria-disabled'], childList: true, characterData: true, subtree: true });

    return () => {
      io.disconnect();
      ro?.disconnect();
      mo.disconnect();
      setVisible(false);
    };
  };

  /* ---------- Lifecycle ---------- */

  const init = (root) => {
    cleanups.get(root)?.();
    const teardown = [initGallery(root), initStickyBar(root)];
    const quantity = root.querySelector('[data-quantity-input]');
    if (quantity) clampQuantity(quantity);
    handleContactReturn(root);
    cleanups.set(root, () => teardown.forEach((fn) => fn()));
  };

  const initWithin = (scope) => {
    if (scope.matches?.('[data-main-product]')) init(scope);
    scope.querySelectorAll('[data-main-product]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document));
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-main-product]').forEach((root) => {
      cleanups.get(root)?.();
      cleanups.delete(root);
    });
  });
})();
